import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import type { Campaign } from '@/lib/types';
import { OPPORTUNITY_DEFS, daysSince } from '@/lib/signals';
import { CHANNEL_LABEL } from '@/lib/messageTemplates';

export interface CampaignActions {
  onSelect: (campaign: Campaign) => void;
  onPause: (campaign: Campaign) => void;
  onResume: (campaign: Campaign) => void;
  onEdit: (campaign: Campaign) => void;
  onDuplicate: (campaign: Campaign) => void;
  onDelete: (campaign: Campaign) => void;
}

interface CampaignCardProps extends CampaignActions {
  campaign: Campaign;
}

const STATUS_LABEL: Record<Campaign['status'], string> = {
  active: 'Activa',
  paused: 'Pausada',
  completed: 'Completada',
  draft: 'Borrador',
};

const statusBadge: Record<Campaign['status'], string> = {
  active: 'bg-ok-tint text-ok-deep [&_.dot]:bg-ok',
  paused: 'bg-warn-tint text-warn-deep [&_.dot]:bg-warn',
  draft: 'bg-warn-tint text-warn-deep [&_.dot]:bg-warn',
  completed: 'bg-soft text-tertiary [&_.dot]:bg-muted',
};

function formatRelative(iso: string | undefined, prefix: string): string {
  const days = daysSince(iso);
  if (days === null) return '';
  if (days === 0) return `${prefix} hoy`;
  if (days === 1) return `${prefix} hace 1 d`;
  if (days < 31) return `${prefix} hace ${days} d`;
  if (days < 365) return `${prefix} hace ${Math.round(days / 30)} m`;
  return `${prefix} hace ${(days / 365).toFixed(1)} años`;
}

export function CampaignCard({
  campaign,
  onSelect,
  onPause,
  onResume,
  onEdit,
  onDuplicate,
  onDelete,
}: CampaignCardProps) {
  const def = OPPORTUNITY_DEFS[campaign.kind];
  const m = campaign.metrics;
  const total = campaign.recipientCount;
  const isDraft = campaign.status === 'draft';
  const dateLabel = isDraft
    ? formatRelative(campaign.createdAt, 'Creada')
    : formatRelative(
        campaign.completedAt ?? campaign.sentAt,
        campaign.completedAt ? 'Cerrada' : 'Enviada'
      );

  const summary = isDraft
    ? `${total} destinatario${total === 1 ? '' : 's'}`
    : `${total} destinatario${total === 1 ? '' : 's'} · ${m.scheduled} agendaron`;

  return (
    <article className="bg-subtle border border-line rounded-lg px-5 py-3.5 flex items-center gap-5 max-[760px]:flex-col max-[760px]:items-stretch">
      <header className="flex-1 min-w-0">
        <div className="flex items-center gap-2.5 text-[11px] text-tertiary flex-wrap mb-1.5">
          <span
            className={`inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.02em] px-2 py-0.5 rounded-full ${statusBadge[campaign.status]}`}
          >
            <span className="dot w-[5px] h-[5px] rounded-full" />
            {STATUS_LABEL[campaign.status]}
          </span>
          <span className="text-[11.5px] text-tertiary before:content-['·'] before:mr-2 before:text-faint">
            {CHANNEL_LABEL[campaign.channel]}
          </span>
          {dateLabel && (
            <span className="text-[11.5px] text-tertiary before:content-['·'] before:mr-2 before:text-faint">
              {dateLabel}
            </span>
          )}
        </div>
        <div className="text-[14.5px] font-semibold tracking-[-0.01em] text-primary truncate">
          {def.title}
        </div>
        <div className="text-xs text-tertiary leading-[1.4] truncate mt-0.5">
          {summary}
        </div>
      </header>

      <footer className="flex items-center gap-1 whitespace-nowrap shrink-0 max-[760px]:justify-end">
        <button
          type="button"
          onClick={() => onSelect(campaign)}
          className="inline-flex items-center gap-1 text-xs font-medium text-primary bg-transparent border border-line rounded-md px-2.5 py-1.5 cursor-pointer transition-colors hover:bg-soft hover:border-line-default"
        >
          Ver detalle <span aria-hidden="true">→</span>
        </button>
        <Menu as="div" className="relative">
          <MenuButton
            aria-label="Más acciones"
            title="Más acciones"
            className="grid place-items-center w-8 h-8 text-tertiary bg-transparent border border-line rounded-md cursor-pointer transition-colors hover:bg-soft hover:text-secondary hover:border-line-default data-[open]:bg-soft data-[open]:text-primary data-[open]:border-line-default"
          >
            <KebabIcon />
          </MenuButton>
          <MenuItems
            anchor={{ to: 'bottom end', gap: 6 }}
            transition
            className="z-[60] w-[176px] origin-top-right bg-surface border border-line rounded-md shadow-elevated p-1 focus:outline-none transition duration-150 ease-out data-[closed]:opacity-0 data-[closed]:scale-95 data-[enter]:data-[closed]:translate-y-1"
          >
            {campaign.status === 'active' && (
              <ActionItem icon={<PauseIcon />} label="Pausar" onClick={() => onPause(campaign)} />
            )}
            {campaign.status === 'paused' && (
              <ActionItem icon={<PlayIcon />} label="Reanudar" onClick={() => onResume(campaign)} />
            )}
            {campaign.status === 'draft' && (
              <ActionItem icon={<EditIcon />} label="Editar" onClick={() => onEdit(campaign)} />
            )}
            <ActionItem icon={<DuplicateIcon />} label="Duplicar" onClick={() => onDuplicate(campaign)} />
            <div className="my-1 border-t border-line" />
            <ActionItem
              icon={<TrashIcon />}
              label="Eliminar"
              onClick={() => onDelete(campaign)}
              destructive
            />
          </MenuItems>
        </Menu>
      </footer>
    </article>
  );
}

interface ActionItemProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  destructive?: boolean;
}

function ActionItem({ icon, label, onClick, destructive }: ActionItemProps) {
  return (
    <MenuItem>
      <button
        type="button"
        onClick={onClick}
        className={`w-full flex items-center gap-2 text-[13px] bg-transparent border-0 px-2.5 py-2 rounded-sm cursor-pointer transition-colors ${
          destructive
            ? 'text-crit-deep data-[focus]:bg-crit-tint'
            : 'text-secondary data-[focus]:bg-soft data-[focus]:text-primary'
        }`}
      >
        {icon}
        <span>{label}</span>
      </button>
    </MenuItem>
  );
}

function KebabIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="12" cy="5" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="12" cy="19" r="1.6" />
    </svg>
  );
}

function PauseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="6" y="5" width="4" height="14" rx="1" />
      <rect x="14" y="5" width="4" height="14" rx="1" />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M7 5l12 7-12 7V5z" />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

function DuplicateIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    </svg>
  );
}
